/**
 * Sharing a public listing (PWA lot 6).
 *
 * The shared link is ALWAYS the public address of the listing on the official domain, in the sender's
 * language: https://ridalo.co/scooters/<id> or /services/<id> (French) and the same path under /en for an
 * English sender (never the address of the current tab: no staging / preview host, no query string, no
 * /dashboard or /admin route, no session data). Each is the very URL a Cloudflare Function
 * (functions/_lib/listingPage.js, functions/en/…) answers with the listing's own title, description and
 * photo (Open Graph) in that language, before any JavaScript runs, and the app then shows the same
 * language (the language is carried by the URL). The Functions return a real 404 for a paused, archived
 * or unapproved listing, so such a link shows nothing about it.
 *
 * A provider's page (category 'provider') follows the same rules: https://ridalo.co/provider/<id>, with its own
 * link preview (functions/provider/). Shared: its commercial name and one generic sentence.
 *
 * What is shared: the listing's public title, one generic sentence in the visitor's language and
 * that URL. Nothing else (no price, provider, booking or personal data).
 */
export const SITE_URL = 'https://ridalo.co';
const ID_RE = /^[A-Za-z0-9_-]{1,64}$/;          // same shape the Function accepts

/** Public path of a listing: scooters live under /scooters, transfers and excursions under /services;
 *  the English page of the same listing is the same path under /en. */
export function listingPath(category, id, lang = 'fr') {
  if (typeof id !== 'string' || !ID_RE.test(id)) return null;
  // A provider's public page (all their scooters): /provider/<id>, under /en for English (Lot 12).
  if (category === 'provider') return `${lang === 'en' ? '/en' : ''}/provider/${id}`;
  return `${lang === 'en' ? '/en' : ''}${category === 'scooter' ? '/scooters' : '/services'}/${id}`;
}

/** Language of a link: the visitor's interface language ('en' for any English variant, else French). */
export const shareLang = (lng) => (String(lng || '').toLowerCase().startsWith('en') ? 'en' : 'fr');

/** Absolute public URL in the sender's language, or null when the identifier is not a valid listing identifier. */
export function buildShareUrl(category, id, lang = 'fr') {
  const path = listingPath(category, id, shareLang(lang));
  return path ? `${SITE_URL}${path}` : null;
}

export function canShareNatively(payload) {
  try {
    if (typeof navigator === 'undefined' || typeof navigator.share !== 'function') return false;
    return typeof navigator.canShare === 'function' && payload ? !!navigator.canShare(payload) : true;
  } catch (e) { return false; }
}

/**
 * Opens the native share sheet (WhatsApp, Messenger, SMS, AirDrop…).
 * Returns 'shared' | 'cancelled' (the person closed the sheet: not an error) | 'failed'.
 */
export async function shareNatively(payload) {
  try {
    await navigator.share(payload);
    return 'shared';
  } catch (e) {
    if (e && (e.name === 'AbortError' || e.name === 'NotAllowedError' && /cancel|abort/i.test(String(e.message)))) return 'cancelled';
    return 'failed';
  }
}

/** Copies text. Returns 'copied' or 'failed'. Clipboard API first, then the legacy selection copy. */
export async function copyText(text) {
  try {
    if (typeof navigator !== 'undefined' && navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
      await navigator.clipboard.writeText(text);
      return 'copied';
    }
  } catch (e) { /* fall through to the legacy copy */ }
  try {
    if (typeof document === 'undefined') return 'failed';
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.cssText = 'position:fixed;top:0;left:0;opacity:0;pointer-events:none;font-size:16px';
    document.body.appendChild(ta);
    ta.select();
    ta.setSelectionRange(0, text.length);
    const ok = document.execCommand && document.execCommand('copy');
    document.body.removeChild(ta);
    return ok ? 'copied' : 'failed';
  } catch (e) { return 'failed'; }
}
